import { Box } from "@chakra-ui/react";
import { RefObject } from "react";
import moment from "moment-timezone";
import { PositionedEvent, CalendarEvent, SlotStyle } from "../types";
import EventDescription from "../primitives/EventDescription";
import CalendarColumn from "../primitives/CalendarColumn";

interface CalendarGridProps {
  containerRef: RefObject<HTMLDivElement>;
  positionedEvents: PositionedEvent[];
  timezone: string;
  dragged: PositionedEvent | null;
  resizing: PositionedEvent | null;
  numDays: number;
  slotHeight: number;
  hours: number[];
  show15Minutes: boolean;
  startDate: Date;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseDown: (e: React.MouseEvent, event: PositionedEvent) => void;
  onResizeStart: (e: React.MouseEvent, event: PositionedEvent) => void;
  onEventClick?: (event: CalendarEvent) => void;
  canCreate: boolean;
  setCreating: (creating: { startY: number; dayIndex: number; startTime: Date } | null) => void;
  getSlotStyle?: (time: Date, columnIndex: number) => SlotStyle | undefined;
}

export default function CalendarGrid({
  containerRef,
  positionedEvents,
  timezone,
  dragged,
  resizing,
  numDays,
  slotHeight,
  hours,
  show15Minutes,
  startDate,
  onMouseMove,
  onMouseUp,
  onMouseDown,
  onResizeStart,
  onEventClick,
  canCreate = false,
  setCreating,
  getSlotStyle,
}: CalendarGridProps) {
  const editingElement = dragged || resizing;

  return (
    <Box
      ref={containerRef}
      position="relative"
      height={`${24 * slotHeight}px`}
      flex={1}
      display="grid"
      gridTemplateColumns={`repeat(${numDays}, 1fr)`}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseDown={(e) => {
        if (!containerRef.current || !canCreate) return;
        const rect = containerRef.current.getBoundingClientRect();
        const dayWidth = rect.width / numDays;
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        const dayIndex = Math.floor(offsetX / dayWidth);
        const minutes = Math.floor((offsetY / slotHeight) * 60 / 15) * 15;

        const startMoment = moment.tz(startDate, timezone).add(dayIndex, "days");
        const totalHours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        startMoment.hours(totalHours).minutes(mins).seconds(0).milliseconds(0);
        setCreating({ startY: offsetY, dayIndex, startTime: startMoment.toDate() });
      }}
    >
      {Array.from({ length: numDays }, (_, dayIndex) => {
        const columnDate = new Date(startDate);
        columnDate.setDate(columnDate.getDate() + dayIndex);
        const columnEvents = positionedEvents.filter((e) => e.dayIndex === dayIndex);

        return (
          <CalendarColumn
            key={dayIndex}
            columnIndex={dayIndex}
            date={columnDate}
            events={columnEvents}
            hours={hours}
            slotHeight={slotHeight}
            show15Minutes={show15Minutes}
            timezone={timezone}
            dragged={dragged}
            resizing={resizing}
            onMouseDown={onMouseDown}
            onResizeStart={onResizeStart}
            onEventClick={onEventClick}
            getSlotStyle={getSlotStyle}
          />
        );
      })}

      {editingElement && (
        <Box
          position="absolute"
          top={`${Math.floor(editingElement.top / (slotHeight / 4)) * (slotHeight / 4)}px`}
          height={`${editingElement.height}px`}
          left={`calc(${(editingElement.dayIndex / numDays) * 100}%)`}
          width={`calc(${100 / numDays}% * 0.98)`}
          bg={editingElement.style?.bg ?? "blue.500"}
          pointerEvents="none"
          borderRadius="4px"
          borderWidth="1px"
          borderColor="gray.300"
          zIndex={999}
        >
          <EventDescription event={editingElement} timezone={timezone} />
        </Box>
      )}
    </Box>
  );
}
